import { useEffect } from 'react';
import { useHistory } from 'react-router-dom';
import { useCurrentUser, useIsManagementUser } from '../context/hooks';
import { UserType } from '../types/user';
import { AdminBasePath } from '../pages/Admin';
import { GroupBasePath } from '../pages/GroupOwner';
import { GROUP_ACCOUNT_TYPES } from '../constants';
import { useCurrentBusiness } from '../states/current-business/hooks';
import { DEFAULT_MANAGEMENT_ROUTE, getFirstPermittedContentRoute } from '../helpers/managementContentRoute';

/**
 * - SystemAdmin -> /admin
 * - Management users -> /group/packages/:businessId
 * - Regular users -> /group
 * - No user -> /login
 */
export const useHomeRedirect = () => {
  const currentUser = useCurrentUser();
  const history = useHistory();
  const isManagementUser = useIsManagementUser();
  const { currentBusinessId, setCurrentBusiness } = useCurrentBusiness();

  useEffect(() => {
    let mounted = true;

    const performRedirect = async () => {
      try {
        if (currentUser) {
          if (currentUser.type === UserType.SystemAdmin) {
            if (mounted) history.push(AdminBasePath);
            return;
          }

          // For management users, redirect to saved business or first business
          if (isManagementUser && currentUser.businesses && currentUser.businesses.length > 0) {
            let targetBusinessId = currentBusinessId;

            if (!targetBusinessId || !currentUser.businesses.some((b) => b._id === targetBusinessId)) {
              targetBusinessId = currentUser.businesses[0]._id;
              setCurrentBusiness(targetBusinessId as string);
            }

            const perm = currentUser.permissions?.find((p) => p.groupId === targetBusinessId);
            const contentRoute = getFirstPermittedContentRoute(perm?.contentPermissions) ?? DEFAULT_MANAGEMENT_ROUTE;

            if (mounted) {
              history.push(`${GroupBasePath}/packages/${GROUP_ACCOUNT_TYPES.BUSINESS}/${targetBusinessId}/${contentRoute}`);
            }
            return;
          }

          if (mounted) history.push(GroupBasePath);
          return;
        }

        if (mounted) history.push('/login');
      } catch (error) {
        // Fallback: still redirect to login on error
        if (mounted) history.push('/login');
      }
    };

    performRedirect();

    return () => {
      mounted = false;
    };
  }, [currentUser, isManagementUser, history, currentBusinessId, setCurrentBusiness]);
};
